import { useEffect, useState, useContext } from "react";
import API from "../api";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { Briefcase, Mail, MapPin, ArrowLeft, Edit3 } from "lucide-react";

function EmployeeDetails() {
  const { id } = useParams();
 const { orgSlug, orgId, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
 
  useEffect(() => {
  if (authLoading || !orgId) return;

  const fetchEmployee = async () => {
    try {
      const res = await API.get(`employees/${id}/`);
      setEmployee(res.data);
    } catch (e) {
  console.error(e);
  setEmployee(null);
} finally {
      setLoading(false);
    }
  };

  fetchEmployee();
}, [id, orgId, authLoading]);

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-slate-50">
       <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-10 space-y-8 animate-in fade-in duration-500">
      {/* Header Actions */}
      <div className="flex justify-between items-center">
        <button onClick={() => navigate(`/${orgSlug}/employees`)} className="flex items-center gap-2 text-slate-500 font-bold hover:text-indigo-600 transition-all">
          <ArrowLeft size={20}/> Directory
        </button>
        <button onClick={() => navigate(`/${orgSlug}/employees/edit/${id}`)} className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black shadow-lg shadow-indigo-100 flex items-center gap-2 hover:bg-indigo-700 transition-all active:scale-95">
          <Edit3 size={18}/> Update Records
        </button>
      </div>

      {/* Hero Header */}
      <div className="bg-slate-900 rounded-[3rem] p-10 text-white flex flex-col md:flex-row items-center md:items-center justify-center md:justify-start gap-10 shadow-2xl min-h-[200px]">
        <div className="h-40 w-40 rounded-[2.5rem] bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-5xl font-black shadow-2xl border-4 border-white/10">
          {employee?.personal?.fullName?.charAt(0)}
        </div>
        <div className="text-center md:text-left flex flex-col justify-center">
          <h1 className="text-5xl font-black tracking-tighter mb-2">{employee?.personal?.fullName}</h1>
        </div>
      </div>

      {/* Profile Sections Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Professional Details */}
        <InfoCard title="Professional" icon={<Briefcase size={20}/>}>
          <DataLine label="Department" value={employee?.professional?.department} />
          <DataLine label="Designation" value={employee?.professional?.designation} />
        </InfoCard>

        {/* Contact Info */}
        <InfoCard title="Contact" icon={<Mail size={20}/>}>
          <DataLine label="Email" value={employee?.email} />
          <DataLine label="Phone" value={employee?.personal?.phone} />
          <DataLine label="Qualification" value={employee?.professional?.qualification} />
        </InfoCard>

        {/* Financial & Location */}
        <InfoCard title="Records" icon={<MapPin size={20}/>}>
          <DataLine label="State" value={employee?.address?.state} />
          <DataLine label="District" value={employee?.address?.district} />
          <DataLine label="Pincode" value={employee?.address?.pincode} />
        </InfoCard>
      </div>
    </div>
  );
}

// Utility Components
const InfoCard = ({ title, icon, children }) => (
  <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100 space-y-6">
    <div className="flex items-center gap-3 text-indigo-600">
      <div className="p-2 bg-indigo-50 rounded-xl">{icon}</div>
      <h3 className="font-black text-xs tracking-widest uppercase">{title} Info</h3>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const DataLine = ({ label, value, isBold }) => (
  <div className="border-b border-slate-50 pb-2">
    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
    <p className={`text-base font-bold ${isBold ? 'text-indigo-600' : 'text-slate-700'}`}>{value || "—"}</p>
  </div>
);

export default EmployeeDetails;